import React, { useEffect, useRef, useState } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import { ToggleGroup, ToggleGroupItem } from "../components/ui/toggle-group";
import { toast } from "sonner";
import { fetchMatrix, saveMatrix, resetMatrix, CATEGORIES } from "../lib/api";
import { Plus, Trash2, Save, RotateCcw, Check } from "lucide-react";

const TIERS = ["A", "B", "C", "D", "E", "F", "G", "H"];

const TIER_COLORS = {
  A: "#1A2340", B: "#7C3AED", C: "#2563EB", D: "#0D9488",
  E: "#16A34A", F: "#CA8A04", G: "#EA580C", H: "#E11D48",
};

export default function RangeMatrix() {
  const [category, setCategory] = useState("car");
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [newRowIdx, setNewRowIdx] = useState(null);
  const newRowRef = useRef(null);
  const [addOpen, setAddOpen] = useState(false);
  const [newRule, setNewRule] = useState({ tier: "A", min: "", max: "", markup: "" });
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);

  useEffect(() => {
    if (newRowIdx !== null && newRowRef.current) {
      newRowRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
      const t = setTimeout(() => setNewRowIdx(null), 2000);
      return () => clearTimeout(t);
    }
  }, [newRowIdx]);

  useEffect(() => {
    setLoading(true);
    setDirty(false);
    fetchMatrix(category)
      .then((r) => setRules(r))
      .catch(() => toast.error("Failed to load matrix"))
      .finally(() => setLoading(false));
  }, [category]);

  const updateRule = (idx, field, value) => {
    setRules((rs) => {
      const next = [...rs];
      next[idx] = { ...next[idx], [field]: field === "tier" ? value : parseFloat(value) || 0 };
      return next;
    });
    setDirty(true);
  };

  const openAddDialog = () => {
    setNewRule({ tier: "A", min: "", max: "", markup: "" });
    setAddOpen(true);
  };

  const confirmAddRow = () => {
    const rule = {
      tier: newRule.tier,
      min: parseFloat(newRule.min) || 0,
      max: parseFloat(newRule.max) || 0,
      markup: parseFloat(newRule.markup) || 0,
    };
    setRules((rs) => {
      setNewRowIdx(rs.length);
      return [...rs, rule];
    });
    setDirty(true);
    setAddOpen(false);
  };

  const deleteRow = (idx) => {
    setRules((rs) => rs.filter((_, i) => i !== idx));
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const saved = await saveMatrix(rules, category);
      setRules(saved);
      setDirty(false);
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2500);
      toast.success("Pricing matrix saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const confirmReset = async () => {
    setResetConfirmOpen(false);
    try {
      const r = await resetMatrix(category);
      setRules(r);
      setDirty(false);
      toast.success("Default values loaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load defaults");
    }
  };

  const orderedRows = rules
    .map((r, idx) => ({ r, idx }))
    .sort((a, b) => {
      const tierDiff = TIERS.indexOf(a.r.tier) - TIERS.indexOf(b.r.tier);
      return tierDiff !== 0 ? tierDiff : a.r.min - b.r.min;
    });

  return (
    <section className="border border-[#E2E8F0] bg-white sharp">
      <div className="border-b border-[#E2E8F0] bg-[#F8F9FA] px-6 py-3 flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="eyebrow text-[#64748B]">Pricing Rules Matrix</div>
          <div className="text-xs text-[#64748B] mt-1">Markup % by tier & price band. Non-overlapping ranges recommended.</div>
        </div>
        <div className="flex items-center gap-2">
          {savedFlash && (
            <span className="text-[12px] text-[#16A34A] flex items-center gap-1 slide-in font-semibold">
              <Check size={14} /> Saved
            </span>
          )}
          {category === "car" && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setResetConfirmOpen(true)}
              className="rounded-sm h-8 text-xs gap-2 border-[#1A2340]/20"
              data-testid="reset-matrix-button"
            >
              <RotateCcw size={13} /> Load Default Values
            </Button>
          )}
          <Button
            size="sm"
            onClick={handleSave}
            disabled={!dirty || saving}
            className="rounded-sm h-8 text-xs gap-2 bg-[#1A2340] hover:bg-[#253060] text-white disabled:opacity-40"
            data-testid="save-matrix-button"
          >
            <Save size={13} /> {saving ? "Saving…" : "Save Rules"}
          </Button>
        </div>
      </div>

      <div className="px-6 py-3 border-b border-[#E2E8F0] bg-white flex items-center gap-3">
        <span className="eyebrow text-[#64748B]">Rule Set</span>
        <ToggleGroup
          type="single"
          value={category}
          onValueChange={(v) => {
            if (!v) return;
            if (dirty && !window.confirm("Discard unsaved changes to the current rule set?")) return;
            setCategory(v);
          }}
          className="justify-start"
        >
          {CATEGORIES.map((c) => (
            <ToggleGroupItem
              key={c.value}
              value={c.value}
              className="rounded-sm h-8 text-xs px-4 border border-[#E2E8F0] data-[state=on]:bg-[#1A2340] data-[state=on]:text-white"
              data-testid={`matrix-category-toggle-${c.value}`}
            >
              {c.label}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      </div>

      <div className="p-6">
        {loading ? (
          <div className="text-sm text-[#64748B]">Loading rules…</div>
        ) : (
          <>
            <div className="border border-[#E2E8F0] overflow-auto max-h-[600px]">
              <table className="tbl">
                <thead>
                  <tr>
                    <th style={{ width: 120 }}>Tier</th>
                    <th style={{ width: 140 }} className="num">Price Floor (€)</th>
                    <th style={{ width: 140 }} className="num">Price Ceiling (€)</th>
                    <th style={{ width: 120 }} className="num">Markup %</th>
                    <th style={{ width: 60 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {orderedRows.map(({ r, idx }, i) => {
                    const prevTier = i > 0 ? orderedRows[i - 1].r.tier : null;
                    const showGroupHeader = r.tier !== prevTier;
                    return (
                      <React.Fragment key={idx}>
                        {showGroupHeader && (
                          <tr key={`group-${r.tier}`} className="bg-[#F8F9FA]">
                            <td
                              colSpan={5}
                              className="!py-2.5 !px-3 border-t-4"
                              style={{ borderTopColor: TIER_COLORS[r.tier] || "#E2E8F0" }}
                            >
                              <div className="flex items-center gap-2">
                                <span
                                  className="w-2.5 h-2.5 rounded-full"
                                  style={{ backgroundColor: TIER_COLORS[r.tier] || "#64748B" }}
                                />
                                <span className="text-[15px] font-bold tracking-wide" style={{ color: TIER_COLORS[r.tier] || "#64748B" }}>
                                  Tier {r.tier}
                                </span>
                              </div>
                            </td>
                          </tr>
                        )}
                        <tr
                          key={idx}
                          ref={idx === newRowIdx ? newRowRef : null}
                          data-testid={`matrix-row-${idx}`}
                          className={idx === newRowIdx ? "bg-[#FEF9C3] transition-colors duration-[2000ms]" : ""}
                        >
                          <td>
                            <Select value={r.tier} onValueChange={(v) => updateRule(idx, "tier", v)}>
                              <SelectTrigger className="rounded-sm h-8 w-24 border-[#E2E8F0] font-mono-data">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {TIERS.map((t) => (
                                  <SelectItem key={t} value={t}>{t}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="num">
                            <Input
                              type="number" step="0.01" min="0"
                              value={r.min}
                              onChange={(e) => updateRule(idx, "min", e.target.value)}
                              className="rounded-sm h-8 w-28 font-mono-data text-right border-[#E2E8F0]"
                            />
                          </td>
                          <td className="num">
                            <Input
                              type="number" step="0.01" min="0"
                              value={r.max}
                              onChange={(e) => updateRule(idx, "max", e.target.value)}
                              className="rounded-sm h-8 w-28 font-mono-data text-right border-[#E2E8F0]"
                            />
                          </td>
                          <td className="num">
                            <Input
                              type="number" step="0.1" min="0" max="1000"
                              value={r.markup}
                              onChange={(e) => updateRule(idx, "markup", e.target.value)}
                              className="rounded-sm h-8 w-24 font-mono-data text-right border-[#E2E8F0]"
                            />
                          </td>
                          <td>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 rounded-sm text-[#E11D48] hover:bg-[#E11D48]/10"
                              onClick={() => deleteRow(idx)}
                              data-testid={`delete-matrix-row-${idx}`}
                            >
                              <Trash2 size={14} />
                            </Button>
                          </td>
                        </tr>
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={openAddDialog}
              className="mt-4 rounded-sm h-9 gap-2 border-dashed border-[#1A2340]/30 hover:bg-[#1A2340] hover:text-white"
              data-testid="add-matrix-row-button"
            >
              <Plus size={14} /> Add Rule
            </Button>
          </>
        )}
      </div>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Pricing Rule</DialogTitle>
            <DialogDescription>
              Define a tier and price band with its markup %.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 space-y-1.5">
              <Label>Tier</Label>
              <Select value={newRule.tier} onValueChange={(v) => setNewRule((n) => ({ ...n, tier: v }))}>
                <SelectTrigger className="rounded-sm h-9 font-mono-data">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIERS.map((t) => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label>Price Floor (€)</Label>
              <Input
                type="number" step="0.01" min="0"
                value={newRule.min}
                onChange={(e) => setNewRule((n) => ({ ...n, min: e.target.value }))}
                className="rounded-sm h-9 font-mono-data"
                placeholder="0"
                autoFocus
              />
            </div>

            <div className="space-y-1.5">
              <Label>Price Ceiling (€)</Label>
              <Input
                type="number" step="0.01" min="0"
                value={newRule.max}
                onChange={(e) => setNewRule((n) => ({ ...n, max: e.target.value }))}
                className="rounded-sm h-9 font-mono-data"
                placeholder="9999"
              />
            </div>

            <div className="col-span-2 space-y-1.5">
              <Label>Markup %</Label>
              <Input
                type="number" step="0.1" min="0" max="1000"
                value={newRule.markup}
                onChange={(e) => setNewRule((n) => ({ ...n, markup: e.target.value }))}
                className="rounded-sm h-9 font-mono-data"
                placeholder="50"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)} className="rounded-sm">
              Cancel
            </Button>
            <Button
              onClick={confirmAddRow}
              className="rounded-sm bg-[#1A2340] hover:bg-[#253060] text-white"
              data-testid="confirm-add-matrix-row"
            >
              <Plus size={14} className="mr-1" /> Add Rule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={resetConfirmOpen} onOpenChange={setResetConfirmOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Load Default Values</DialogTitle>
            <DialogDescription>
              Load default values for Car / Motor Tires? This will replace the current Pricing Rules Matrix.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetConfirmOpen(false)} className="rounded-sm">
              Cancel
            </Button>
            <Button
              onClick={confirmReset}
              className="rounded-sm bg-[#1A2340] hover:bg-[#253060] text-white"
              data-testid="confirm-reset-matrix"
            >
              <RotateCcw size={14} className="mr-1" /> Load Default Values
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
